import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { currentActivityOption, EntityFormOption, InjectedOptions, optionId } from '../../../entity-form/entity-form.utils';
import {
  conceptPrefix,
  CreditDialogData,
  creditTitle,
  displayDate,
  EMPTY_GUID,
  fromApiDate,
  roundMoney,
  sortByDateDesc,
  stripConceptPrefix,
  toApiDate,
} from '../../models/credit-shared.model';
import { CreditOperationsService } from '../../services/credit-operations.service';
import { CreditLookupsService, CreditProjectLookups } from '../../services/credit-lookups.service';
import { CreditFormLookupsFacade } from '../../services/credit-form-lookups.facade';

@Component({
  selector: 'app-credit-installments-dialog',
  templateUrl: './credit-installments-dialog.component.html',
  styleUrls: ['../../styles/credit-dialog.scss'],
  standalone: false,
})
export class CreditInstallmentsDialogComponent implements OnInit {
  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  changed = false;
  private requestedEditDone = false;

  credit: any;
  items: any[] = [];
  editing: any | null = null;

  activities: EntityFormOption[] = [];
  bankAccounts: EntityFormOption[] = [];
  taxes: EntityFormOption[] = [];
  projects: EntityFormOption[] = [];
  expenseProjectTypes: EntityFormOption[] = [];
  isProjectEnable = false;

  form: UntypedFormGroup;

  private readonly injected = new InjectedOptions();

  static open(dialog: MatDialog, data: CreditDialogData): Observable<boolean> {
    return dialog.open<CreditInstallmentsDialogComponent, CreditDialogData, boolean>(CreditInstallmentsDialogComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreditInstallmentsDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditDialogData,
    private operations: CreditOperationsService,
    private lookups: CreditLookupsService,
    private formLookups: CreditFormLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.credit = { ...data.credit };
    this.form = this.fb.group({
      fecha: [new Date()],
      concepto: [''],
      importeCapital: [null],
      intereses: [null],
      tax: [null],
      activity: [null],
      bankAccount: [null],
      project: [null],
      expenseProjectType: [null],
    });
  }

  get isEmployee(): boolean {
    return this.data.kind === 'employee';
  }

  get subtitle(): string {
    return creditTitle(this.data.kind, this.data.credit);
  }

  get yearHint(): string {
    return this.isEmployee ? '' : 'Ejercicio ' + ActualSelected.yearSelected;
  }

  get initialAmount(): number {
    return roundMoney(this.credit.importeInicial);
  }

  get pendingAmount(): number {
    return roundMoney(this.credit.importePendiente);
  }

  get collectedAmount(): number {
    return roundMoney(Math.max(0, this.initialAmount - this.pendingAmount));
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean { return optionId(this.form.get('bankAccount').value) === null; }

  get invalidCapital(): boolean {
    const value = Number(this.form.get('importeCapital').value);
    return this.isEmployee ? !value : !value || value <= 0;
  }

  get showProjects(): boolean {
    return !this.isEmployee && this.isProjectEnable && this.projects.length > 0;
  }

  get showExpenseProjectTypes(): boolean {
    return !this.isEmployee && this.isProjectEnable && this.expenseProjectTypes.length > 0;
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  ngOnInit(): void {
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  assignPending(): void {
    this.control('importeCapital').setValue(this.pendingAmount > 0 ? this.pendingAmount : null);
  }

  startEdit(item: any): void {
    this.editing = item;
    this.submitted = false;
    this.injected.clear();
    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      concepto: stripConceptPrefix(item.concepto, conceptPrefix(this.data.kind, this.data.credit)),
      importeCapital: item.importeCapital != null ? Number(item.importeCapital) : null,
      intereses: item.intereses != null ? Number(item.intereses) : null,
      tax: this.injected.ensure(this.taxes, Number(item.taxesId) || null, item.taxesName),
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
      bankAccount: this.injected.ensure(this.bankAccounts, item.bankAccountsUid, item.bankAccountInfo),
      project: this.injected.ensure(this.projects, item.projectUid, item.projectName),
      expenseProjectType: this.injected.ensure(this.expenseProjectTypes, item.expenseProjectTypeUid, item.expenseProjectTypeName),
    }, { emitEvent: false });
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (this.missingDate || this.missingConcept || this.invalidCapital || this.missingActivity || this.missingBankAccount) {
      return;
    }

    const isEdit = !!this.editing;
    const payload = this.buildPayload();
    this.saving = true;
    this.savingMessage = isEdit ? 'Actualizando cobro…' : 'Registrando cobro…';
    this.dialogRef.disableClose = true;

    this.operations.saveInstallment(this.data.kind, payload, isEdit)
      .then(ok => { if (ok !== true) throw new Error(); })
      .then(() => {
        this.changed = true;
        this.notify.ok(isEdit ? 'Cobro actualizado correctamente' : 'Cobro registrado correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.reloadAfterChange();
      })
      .catch(error => {
        const serverMessage = error && error.error && error.error.message;
        this.notify.error(serverMessage || 'No se pudo guardar el cobro');
      })
      .then(() => {
        this.saving = false;
        this.dialogRef.disableClose = false;
      });
  }

  remove(item: any): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar cobro',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el cobro y su asiento contable, y el capital volverá a sumarse al importe pendiente del crédito. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando cobro…',
      errorText: 'No se pudo eliminar el cobro',
      action: () => this.operations.deleteInstallment(this.data.kind, item.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Cobro eliminado correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.reloadAfterChange();
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, item: any): string {
    return item.uid;
  }

  itemDate(item: any): string {
    return displayDate(item.fecha);
  }

  itemMeta(item: any): string {
    const parts: string[] = [stripConceptPrefix(item.concepto, conceptPrefix(this.data.kind, this.data.credit))];
    if (Number(item.intereses)) parts.push('Intereses ' + Utils.FormatMoney(item.intereses) + ' €');
    if (item.bankAccountInfo) parts.push(item.bankAccountInfo);
    return parts.filter(Boolean).join(' · ');
  }


  private reloadAfterChange(): void {
    this.loadItems(false);
    this.operations.get(this.data.kind, this.data.credit.uid)
      .then(credit => {
        if (credit) {
          this.credit = { ...this.credit, importeInicial: credit.importeInicial, importePendiente: credit.importePendiente };
        }
      })
      .catch(() => { });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.items.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    const projects: Promise<CreditProjectLookups> = this.isEmployee
      ? Promise.resolve({ enabled: false, projects: [], expenseProjectTypes: [] })
      : this.lookups.projects();
    const taxes: Promise<any[]> = this.isEmployee ? Promise.resolve([]) : this.lookups.interestTaxes();

    Promise.all([
      this.operations.listInstallments(this.data.kind, this.data.credit.uid),
      this.lookups.activities(),
      this.formLookups.bankAccounts(),
      taxes,
      projects,
    ])
      .then(([list, activities, bankAccounts, taxList, projectLookups]) => {
        this.activities = (activities || [])
          .filter(activity => activity && activity.uid && activity.uid !== EMPTY_GUID)
          .map(activity => ({ id: activity.uid, display: activity.name || '' }));
        this.bankAccounts = bankAccounts;
        this.taxes = (taxList || []).map(tax => ({ id: tax.id, display: tax.name || '' }));
        this.isProjectEnable = projectLookups.enabled;
        this.projects = (projectLookups.projects || []).map(project => ({ id: project.id, display: project.display || '' }));
        this.expenseProjectTypes = (projectLookups.expenseProjectTypes || []).map(type => ({ id: type.id, display: type.display || '' }));
        this.items = sortByDateDesc(list, 'fecha');
        this.applySingleOptionDefaults();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private loadItems(initial: boolean): void {
    if (initial) this.ready = false;
    this.operations.listInstallments(this.data.kind, this.data.credit.uid)
      .then(list => {
        this.items = sortByDateDesc(list, 'fecha');
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) this.loadError = true;
        else this.notify.error('No se pudieron recargar los cobros');
      })
      .then(() => { this.ready = true; });
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;
    if (optionId(this.form.get('activity').value) === null) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (optionId(this.form.get('bankAccount').value) === null && this.bankAccounts.length === 1) {
      this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
    }
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const payload = this.editing ? { ...this.editing } : this.operations.newInstallment(this.data.kind);
    payload[this.operations.parentKey(this.data.kind)] = this.data.credit.uid;
    payload.fecha = toApiDate(value.fecha);
    payload.concepto = value.concepto;
    payload.importeCapital = Number(value.importeCapital) || 0;
    payload.intereses = Number(value.intereses) || 0;
    payload.activitiesUid = optionId(value.activity);
    payload.bankAccountsUid = optionId(value.bankAccount);
    if (!this.isEmployee) {
      payload.taxesId = Number(optionId(value.tax)) || 0;
      payload.projectUid = optionId(value.project);
      payload.expenseProjectTypeUid = optionId(value.expenseProjectType);
    }
    return payload;
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.form.reset({
      fecha: new Date(),
      concepto: '',
      importeCapital: null,
      intereses: null,
      tax: null,
      activity: null,
      bankAccount: null,
      project: null,
      expenseProjectType: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private itemDetails(item: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: displayDate(item.fecha) });
    details.push({ label: 'Capital', value: Utils.FormatMoney(Number(item.importeCapital) || 0) + ' €' });
    if (Number(item.intereses)) details.push({ label: 'Intereses', value: Utils.FormatMoney(Number(item.intereses) || 0) + ' €' });
    if (item.bankAccountInfo) details.push({ label: 'Cuenta', value: item.bankAccountInfo });
    return details;
  }
}
